import {
  AxiosAdapter,
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';

import { ORBIT_SESSIONS_PATH } from 'api/orbit';
import API from 'utils/request';

import { OrbitSession } from '../../types';
import { ORBIT_L4_DEMO_SESSION_ID } from './constants';
import { demoSessionRecord, handleDemoRequest, isDemoRequest } from './engine';

/*
  The demo chat runs on the real Orbit client: this swaps the axios adapter for its requests only,
  so every hook, poll and mutation is the production code path and nothing reaches the server.
*/

let enabled = false;

/** On only while `orbit_layer_4` is; demo requests made while off still never leave the browser. */
export const setOrbitL4DemoEnabled = (next: boolean): void => {
  enabled = next;
};

export const isOrbitL4DemoEnabled = (): boolean => enabled;

const NOT_FOUND = { status: 404, data: { code: 40410, message: 'Orbit request not found' } };

const demoAdapter: AxiosAdapter = async config => {
  const method = (config.method ?? 'get').toLowerCase();
  const { status, data } = enabled
    ? handleDemoRequest(method, config.url ?? '', config.data)
    : NOT_FOUND;
  const response: AxiosResponse = {
    data,
    status,
    statusText: '',
    headers: {},
    config,
    request: {}
  };

  if (status >= 400) {
    throw new AxiosError(
      `Request failed with status code ${status}`,
      status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
      config,
      {},
      response
    );
  }
  return response;
};

const isUnfilteredFirstPage = (url: string): boolean => {
  const { pathname, searchParams } = new URL(url, 'http://orbit.demo');
  return (
    pathname === ORBIT_SESSIONS_PATH &&
    !searchParams.has('q') &&
    !searchParams.has('pinned') &&
    !searchParams.has('workspace') &&
    (searchParams.get('page') ?? '1') === '1'
  );
};

/** Lists the demo chat first in the chat history while the flag is on. */
const withDemoSession = (response: AxiosResponse): AxiosResponse => {
  const rows = response?.data?.data;
  if (
    !enabled ||
    (response.config.method ?? 'get').toLowerCase() !== 'get' ||
    !isUnfilteredFirstPage(response.config.url ?? '') ||
    !Array.isArray(rows) ||
    rows.some((row: OrbitSession) => row.session_id === ORBIT_L4_DEMO_SESSION_ID)
  ) {
    return response;
  }
  return { ...response, data: { ...response.data, data: [demoSessionRecord(), ...rows] } };
};

const installed = new WeakSet<AxiosInstance>();

export const installOrbitL4DemoTransport = (instance: AxiosInstance = API): void => {
  if (installed.has(instance)) {
    return;
  }
  installed.add(instance);
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (isDemoRequest(method, config.url ?? '', config.data)) {
      return { ...config, adapter: demoAdapter };
    }
    return config;
  });
  instance.interceptors.response.use(withDemoSession);
};
